import Pokemon from './Pokemon.js';

export default class Pokedex {
    constructor({ accessible = true, caught = {}, seen = {} }) {
        this.accessible = accessible;
        this.caught = {};
        this.seen = {};

        for (let i = 1; i <= 386; i++) {
            this.caught[i] = caught[i] || null;
            this.seen[i] = seen[i] || null;
        }

        this.regions = {};

        // Grab pokedex data from local storage
        try {
            const pokedexDataJson = localStorage.getItem('pokedex_data');
            if (pokedexDataJson) {
                const pokedexData = JSON.parse(pokedexDataJson);

                // Iterate over all keys (identifiers)
                for (const identifier of Object.keys(pokedexData)) {
                    const entries = pokedexData[identifier].entries;

                    // Add key to regions
                    this.regions[identifier] = {
                        seen: {},
                        caught: {},
                    };

                    // Prepopulate seen and caught objects based on entries length
                    // Assuming entries is an array where each entry has a pokemonId or id
                    if (Array.isArray(entries)) {
                        for (const entry of entries) {
                            const pokemonId = entry.pokemonId || entry.id || entry.pokemon?.id;
                            if (pokemonId) {
                                this.regions[identifier].seen[pokemonId] = null;
                                this.regions[identifier].caught[pokemonId] = null;
                            }
                        }
                    }
                }
            }
        } catch (error) {
            console.error(`Error loading pokedex data from localStorage: ${error.message}`);
        }
    }

    addCaughtPokemon(pokemon) {
        // Iterate over all regions first
        for (const identifier of Object.keys(this.regions)) {
            const region = this.regions[identifier];
            if (region.caught[pokemon.id] === null) {
                region.caught[pokemon.id] = pokemon.rawPokemonData;
            }
        }

        // Also update top-level caught
        if (this.caught[pokemon.id] === null) {
            this.caught[pokemon.id] = pokemon.rawPokemonData;
        }
    }

    addSeenPokemon(pokemon) {
        // Iterate over all regions first
        for (const identifier of Object.keys(this.regions)) {
            const region = this.regions[identifier];
            if (region.seen[pokemon.id] === null) {
                region.seen[pokemon.id] = pokemon.rawPokemonData;
            }
        }

        // Also update top-level seen
        if (this.seen[pokemon.id] === null) {
            this.seen[pokemon.id] = pokemon.rawPokemonData;
        }
    }

    isInPokedex(pokemon) {
        return !!this.caught[pokemon.id];
    }

    serialize() {
        console.log(`Serializing ${this.constructor.name}`);
        const serializedData = {
            accessible: this.accessible,
            caught: Object.keys(this.caught)
                .map((idString) => {
                    if (this.caught[parseInt(idString)]) {
                        return parseInt(idString);
                    }
                    return null;
                })
                .filter((p) => p !== null),
            seen: Object.keys(this.seen)
                .map((idString) => {
                    if (this.seen[parseInt(idString)]) {
                        return parseInt(idString);
                    }
                    return null;
                })
                .filter((p) => p !== null),
        };
        return serializedData;
    }

    static deserialize(data) {
        // Create a new Pokedex instance (constructor will initialize all slots to null)
        const pokedex = new Pokedex({
            accessible: data.accessible,
            caught: {},
            seen: {},
        });

        // Use addCaughtPokemon and addSeenPokemon methods to restore saved data
        // This ensures both regions and top-level objects are updated correctly
        data.caught.forEach((id) => {
            const rawPokemonData = Pokemon.getRawPokemonData(id);
            const mockPokemon = {
                id: id,
                rawPokemonData: rawPokemonData,
            };
            pokedex.addCaughtPokemon(mockPokemon);
            // Also mark as seen since caught pokemon are also seen
            pokedex.addSeenPokemon(mockPokemon);
        });

        // Add seen pokemon that weren't caught
        data.seen.forEach((id) => {
            // Skip if already caught (already added above)
            if (!data.caught.includes(id)) {
                const rawPokemonData = Pokemon.getRawPokemonData(id);
                const mockPokemon = {
                    id: id,
                    rawPokemonData: rawPokemonData,
                };
                pokedex.addSeenPokemon(mockPokemon);
            }
        });

        return pokedex;
    }
}
